import { QrCode, ShoppingBag, CreditCard, Clock } from "lucide-react";
import { useT } from "../i18n";
import { appUrl } from "../lib/appUrl";
import { btnPrimaryLg, btnSecondaryLg } from "../lib/ui";
import { BarmaidMark } from "./BarmaidLogo";

export default function Hero() {
  const translate = useT();

  return (
    <section className="relative overflow-hidden" aria-labelledby="hero-heading">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,var(--hero-glow),transparent_65%)]" />
      <div className="relative mx-auto grid max-w-6xl gap-12 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2 lg:items-center lg:gap-16">
        <div>
          <h1 id="hero-heading" className="font-display text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
            {translate("heroTitle")}{" "}
            <span className="text-brand-gradient">{translate("heroTitleAccent")}</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {translate("heroSubtitle")}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a href={appUrl("/admin/register")} className={btnPrimaryLg}>
              {translate("heroCtaPrimary")}
            </a>
            <a href="#how-it-works" className={btnSecondaryLg}>
              {translate("heroCtaSecondary")}
            </a>
          </div>
        </div>

        <div className="relative mx-auto w-full max-w-sm lg:max-w-none">
          <div className="relative mx-auto w-[280px] rounded-[2.5rem] border border-[var(--border-subtle)] bg-surface p-3 shadow-elevated ring-brand sm:w-[300px]">
            <div className="overflow-hidden rounded-[2rem] bg-background">
              <div className="bg-brand-soft px-4 py-5 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface shadow-card">
                  <BarmaidMark size={28} />
                </div>
                <p className="font-display text-sm font-semibold">Terrace Bar</p>
                <p className="mt-0.5 text-xs text-muted-foreground">Table 12 · Pickup</p>
              </div>
              <div className="space-y-3 p-4">
                <div className="flex items-center gap-3 rounded-xl bg-brand-soft p-3">
                  <QrCode className="h-5 w-5 shrink-0 text-accent" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold">Scan &amp; order</p>
                    <p className="truncate text-[10px] text-muted-foreground">No app needed</p>
                  </div>
                </div>
                <MockMenuRow icon={ShoppingBag} label="Margherita pizza" price="42 zł" />
                <MockMenuRow icon={ShoppingBag} label="Aperol spritz" price="28 zł" />
                <div className="flex items-center justify-between rounded-xl border border-[var(--border-subtle)] bg-surface-light px-3 py-2">
                  <span className="text-xs font-medium">Cart · 2 items</span>
                  <span className="text-xs font-bold text-accent">70 zł</span>
                </div>
                <div className="flex items-center justify-center gap-2 rounded-xl bg-brand-gradient py-2.5 text-xs font-semibold text-white shadow-glow">
                  <CreditCard className="h-3.5 w-3.5" aria-hidden />
                  Pay with card or BLIK
                </div>
              </div>
              <div className="flex items-center justify-center gap-2 border-t border-[var(--border-subtle)] px-4 py-3 text-[10px] text-muted-foreground">
                <Clock className="h-3 w-3" aria-hidden />
                Live order tracking
              </div>
            </div>
          </div>
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-brand-soft blur-2xl" aria-hidden />
          <div className="absolute -bottom-6 -left-6 h-32 w-32 rounded-full bg-brand-soft blur-3xl" aria-hidden />
        </div>
      </div>
    </section>
  );
}

function MockMenuRow({
  icon: Icon,
  label,
  price,
}: {
  icon: typeof ShoppingBag;
  label: string;
  price: string;
}) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-[var(--border-subtle)] bg-surface px-3 py-2">
      <div className="flex items-center gap-2">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="text-xs">{label}</span>
      </div>
      <span className="text-xs font-medium">{price}</span>
    </div>
  );
}
